import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  HomeIcon,
  TruckIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
  CubeIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';
import SearchableSelect from '../../../components/shared/SearchableSelect';
import { VEHICLE_DATA, VEHICLE_MAKES, getModelYears, calculateEstimatedValue, getVehicleCategory } from '../../../data/vehicleData';

const ASSET_TYPES = [
  { value: '', label: 'Please Select an Option', icon: CubeIcon, color: 'text-gray-400' },
  { value: 'real_estate', label: 'Real Estate', icon: HomeIcon, color: 'text-blue-600' },
  { value: 'vehicle', label: 'Vehicle', icon: TruckIcon, color: 'text-green-600' },
  { value: 'investment', label: 'Investment', icon: ChartBarIcon, color: 'text-purple-600' },
  { value: 'business', label: 'Business Asset', icon: BuildingOfficeIcon, color: 'text-orange-600' },
  { value: 'electronics', label: 'Electronics', icon: ComputerDesktopIcon, color: 'text-indigo-600' },
  { value: 'cash', label: 'Cash/Savings', icon: BanknotesIcon, color: 'text-emerald-600' },
  { value: 'personal', label: 'Personal Property', icon: CubeIcon, color: 'text-gray-600' }
];

const CONDITION_OPTIONS = [
  { value: '', label: 'Please Select an Option' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
];

const FINANCING_TYPES = [
  { value: '', label: 'Please Select an Option' },
  { value: 'cash', label: 'Cash Purchase' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'business_loan', label: 'Business Loan' },
  { value: 'heloc', label: 'HELOC' },
  { value: 'other', label: 'Other Financing' }
];

const PROPERTY_TYPES = [
  { value: '', label: 'Please Select an Option' },
  { value: 'single_family', label: 'Single Family Home' },
  { value: 'condo', label: 'Condominium' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi_family', label: 'Multi-Family' },
  { value: 'land', label: 'Land/Lot' },
  { value: 'commercial', label: 'Commercial Property' }
];

const DEPRECIATION_METHODS = [
  { value: '', label: 'Please Select an Option' },
  { value: 'none', label: 'No Depreciation' },
  { value: 'straight-line', label: 'Straight Line' },
  { value: 'declining-balance', label: 'Declining Balance' },
  { value: 'market-based', label: 'Market Based' }
];

const AssetCreateModal = ({ isOpen, onClose, onSave, loading = false, editingAsset = null }) => {
  const [formData, setFormData] = useState({
    // Basic fields
    name: '',
    type: '',
    currentValue: '',
    purchasePrice: '',
    purchaseDate: '',
    condition: '',
    location: '',
    notes: '',
    isShared: false,
    // Professional fields
    costBasis: '',
    improvements: '',
    depreciationMethod: '',
    usefulLife: '',
    liquidity: '',
    insuranceCoverage: '',
    insuranceProvider: '',
    lastAppraisal: '',
    appraisalValue: '',
    incomeGenerated: '',
    taxClassification: '',
    // Financing fields
    financingType: '',
    downPayment: '',
    loanBalance: '',
    monthlyPayment: '',
    interestRate: '',
    loanTerm: '',
    lenderName: '',
    // Real estate specific
    propertyAddress: '',
    propertyType: '',
    squareFootage: '',
    yearBuilt: '',
    bedrooms: '',
    bathrooms: '',
    propertyTaxes: '',
    hoaFees: '',
    rentalIncome: '',
    // Vehicle specific  
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vinNumber: '',
    mileage: '',
    licensePlate: ''
  });

  // Populate form when editing an asset
  useEffect(() => {
    if (editingAsset) {
      console.log('🔧 EDIT MODAL - Populating form with asset data:', editingAsset);
      console.log('🔧 EDIT MODAL - Key fields:', {
        depreciationMethod: editingAsset.depreciationMethod,
        purchaseDate: editingAsset.purchaseDate
      });
      setFormData({
        // Basic fields
        name: editingAsset.name || '',
        type: editingAsset.type || '',
        currentValue: editingAsset.currentValue || '',
        purchasePrice: editingAsset.purchasePrice || '',
        purchaseDate: editingAsset.purchaseDate || '',
        condition: editingAsset.condition || '',
        location: editingAsset.location || '',
        notes: editingAsset.notes || '',
        isShared: Boolean(editingAsset.isShared),
        // Professional fields
        costBasis: editingAsset.costBasis || '',
        improvements: editingAsset.improvements || '',
        depreciationMethod: editingAsset.depreciationMethod || '',
        usefulLife: editingAsset.usefulLife || '',
        liquidity: editingAsset.liquidity || '',
        insuranceCoverage: editingAsset.insuranceCoverage || '',
        insuranceProvider: editingAsset.insuranceProvider || '',
        lastAppraisal: editingAsset.lastAppraisal || '',
        appraisalValue: editingAsset.appraisalValue || '',
        incomeGenerated: editingAsset.incomeGenerated || '',
        taxClassification: editingAsset.taxClassification || '',
        // Financing fields
        financingType: editingAsset.financingType || '',
        downPayment: editingAsset.downPayment || '',
        loanBalance: editingAsset.loanBalance || '',
        monthlyPayment: editingAsset.monthlyPayment || '',
        interestRate: editingAsset.interestRate || '',
        loanTerm: editingAsset.loanTerm || '',
        lenderName: editingAsset.lenderName || '',
        // Real estate specific
        propertyAddress: editingAsset.propertyAddress || '',
        propertyType: editingAsset.propertyType || '',
        squareFootage: editingAsset.squareFootage || '',
        yearBuilt: editingAsset.yearBuilt || '',
        bedrooms: editingAsset.bedrooms || '',
        bathrooms: editingAsset.bathrooms || '',
        propertyTaxes: editingAsset.propertyTaxes || '',
        hoaFees: editingAsset.hoaFees || '',
        rentalIncome: editingAsset.rentalIncome || '',
        // Vehicle specific  
        vehicleMake: editingAsset.vehicleMake || '',
        vehicleModel: editingAsset.vehicleModel || '',
        vehicleYear: editingAsset.vehicleYear || '',
        vinNumber: editingAsset.vinNumber || '',
        mileage: editingAsset.mileage || '',
        licensePlate: editingAsset.licensePlate || ''
      });
    } else {
      // Reset form when not editing
      setFormData({
        // Basic fields
        name: '',
        type: '',
        currentValue: '',
        purchasePrice: '',
        purchaseDate: '',
        condition: '',
        location: '',
        notes: '',
        isShared: false,
        // Professional fields
        costBasis: '',
        improvements: '',
        depreciationMethod: '',
        usefulLife: '',
        liquidity: '',
        insuranceCoverage: '',
        insuranceProvider: '',
        lastAppraisal: '',
        appraisalValue: '',
        incomeGenerated: '',
        taxClassification: '',
        // Financing fields
        financingType: '',
        downPayment: '',
        loanBalance: '',
        monthlyPayment: '',
        interestRate: '',
        loanTerm: '',
        lenderName: '',
        // Real estate specific
        propertyAddress: '',
        propertyType: '',
        squareFootage: '',
        yearBuilt: '',
        bedrooms: '',
        bathrooms: '',
        propertyTaxes: '',
        hoaFees: '',
        rentalIncome: '',
        // Vehicle specific  
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        vinNumber: '',
        mileage: '',
        licensePlate: ''
      });
    }
  }, [editingAsset]);

  const [expandedSections, setExpandedSections] = useState(new Set(['basic']));
  const [errors, setErrors] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [estimatedValue, setEstimatedValue] = useState(null);
  const [showEstimation, setShowEstimation] = useState(false);

  // Update available models when make changes
  useEffect(() => {
    if (formData.vehicleMake && VEHICLE_DATA[formData.vehicleMake]) {
      setAvailableModels(VEHICLE_DATA[formData.vehicleMake]);
      
      // Clear model if it's not valid for the new make
      if (formData.vehicleModel && !VEHICLE_DATA[formData.vehicleMake].includes(formData.vehicleModel)) {
        setFormData(prev => ({ ...prev, vehicleModel: '' }));
      }
    } else {
      setAvailableModels([]);
    }
  }, [formData.vehicleMake]);

  // Auto-calculate estimated value when vehicle details change
  useEffect(() => {
    if (formData.type === 'vehicle' && 
        formData.vehicleMake && 
        formData.vehicleModel && 
        formData.vehicleYear && 
        formData.purchasePrice) {
      
      const estimated = calculateEstimatedValue(
        parseFloat(formData.purchasePrice) || 0,
        formData.vehicleMake,
        formData.vehicleModel,
        parseInt(formData.vehicleYear) || new Date().getFullYear(),
        parseInt(formData.mileage) || 0,
        formData.condition || 'good'
      );
      
      setEstimatedValue(estimated);
      setShowEstimation(true);
    } else {
      setEstimatedValue(null);
      setShowEstimation(false);
    }
  }, [formData.type, formData.vehicleMake, formData.vehicleModel, formData.vehicleYear, 
      formData.purchasePrice, formData.mileage, formData.condition]);

  // Effect to populate form when editing an asset
  React.useEffect(() => {
    if (editingAsset) {
      // Map asset names to appropriate types for better UX
      let assetType = editingAsset.type || '';
      
      // Auto-detect asset type based on name if type is missing
      if (!assetType && editingAsset.name) {
        const name = editingAsset.name.toLowerCase();
        if (name.includes('car') || name.includes('vehicle') || name.includes('auto')) {
          assetType = 'vehicle';
        } else if (name.includes('mortgage') || name.includes('home') || name.includes('house')) {
          assetType = 'real_estate';
        } else if (name.includes('loan') && (name.includes('home') || name.includes('house') || name.includes('mortgage'))) {
          assetType = 'real_estate';
        } else if (name.includes('investment') || name.includes('stock') || name.includes('bond')) {
          assetType = 'investment';
        } else if (name.includes('business') || name.includes('company')) {
          assetType = 'business';
        } else if (name.includes('cash') || name.includes('savings') || name.includes('checking')) {
          assetType = 'cash';
        } else {
          assetType = 'personal';
        }
      }
      
      setFormData({
        name: editingAsset.name || '',
        type: assetType,
        currentValue: editingAsset.currentValue || '',
        purchasePrice: editingAsset.purchasePrice || '',
        purchaseDate: editingAsset.purchaseDate ? editingAsset.purchaseDate.split('T')[0] : '',
        condition: editingAsset.condition || 'good',
        location: editingAsset.location || '',
        notes: editingAsset.notes || '',
        isShared: editingAsset.isShared || false,
        costBasis: editingAsset.costBasis || '',
        improvements: editingAsset.improvements || '',
        depreciationMethod: editingAsset.depreciationMethod || '',
        usefulLife: editingAsset.usefulLife || '',
        liquidity: editingAsset.liquidity || '',
        insuranceCoverage: editingAsset.insuranceCoverage || '',
        insuranceProvider: editingAsset.insuranceProvider || '',
        lastAppraisal: editingAsset.lastAppraisal ? editingAsset.lastAppraisal.split('T')[0] : '',
        appraisalValue: editingAsset.appraisalValue || '',
        incomeGenerated: editingAsset.incomeGenerated || '',
        taxClassification: editingAsset.taxClassification || '',
        financingType: editingAsset.financingType || '',
        downPayment: editingAsset.downPayment || '',
        loanBalance: editingAsset.loanBalance || '',
        monthlyPayment: editingAsset.monthlyPayment || '',
        interestRate: editingAsset.interestRate || '',
        loanTerm: editingAsset.loanTerm || '',
        lenderName: editingAsset.lenderName || '',
        propertyAddress: editingAsset.propertyAddress || '',
        propertyType: editingAsset.propertyType || 'single_family',
        squareFootage: editingAsset.squareFootage || '',
        yearBuilt: editingAsset.yearBuilt || '',
        bedrooms: editingAsset.bedrooms || '',
        bathrooms: editingAsset.bathrooms || '',
        propertyTaxes: editingAsset.propertyTaxes || '',
        hoaFees: editingAsset.hoaFees || '',
        rentalIncome: editingAsset.rentalIncome || '',
        vehicleMake: editingAsset.vehicleMake || '',
        vehicleModel: editingAsset.vehicleModel || '',
        vehicleYear: editingAsset.vehicleYear || '',
        vinNumber: editingAsset.vinNumber || '',
        mileage: editingAsset.mileage || '',
        licensePlate: editingAsset.licensePlate || ''
      });
    } else {
      // Reset form for new asset
      setFormData({
        name: '',
        type: 'personal',
        currentValue: '',
        purchasePrice: '',
        purchaseDate: '',
        condition: 'good',
        location: '',
        notes: '',
        isShared: false,
        costBasis: '',
        improvements: '',
        depreciationMethod: 'none',
        usefulLife: '',
        liquidity: '',
        insuranceCoverage: '',
        insuranceProvider: '',
        lastAppraisal: '',
        appraisalValue: '',
        incomeGenerated: '',
        taxClassification: '',
        financingType: '',
        downPayment: '',
        loanBalance: '',
        monthlyPayment: '',
        interestRate: '',
        loanTerm: '',
        lenderName: '',
        propertyAddress: '',
        propertyType: 'single_family',
        squareFootage: '',
        yearBuilt: '',
        bedrooms: '',
        bathrooms: '',
        propertyTaxes: '',
        hoaFees: '',
        rentalIncome: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        vinNumber: '',
        mileage: '',
        licensePlate: ''
      });
    }
  }, [editingAsset]);

  const toggleSection = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleInputChange = (field, value) => {
    // Debug: Log critical field changes
    if (field === 'depreciationMethod' || field === 'purchaseDate') {
      console.log(`🔧 MODAL INPUT CHANGE - ${field}:`, value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Asset name is required';
    }
    
    if (!formData.type || formData.type === '') {
      newErrors.type = 'Asset type is required';
    }
    
    if (!formData.currentValue || parseFloat(formData.currentValue) < 0) {
      newErrors.currentValue = 'Current value must be a positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Debug: Log the current form data
    console.log('🔍 MODAL SUBMIT - Current formData:', {
      name: formData.name,
      type: formData.type,
      currentValue: formData.currentValue,
      depreciationMethod: formData.depreciationMethod,
      purchaseDate: formData.purchaseDate,
      allFormData: formData
    });

    // Convert numeric fields
    const assetData = {
      ...formData,
      currentValue: parseFloat(formData.currentValue) || 0,
      purchasePrice: parseFloat(formData.purchasePrice) || 0,
      costBasis: parseFloat(formData.costBasis) || 0,
      improvements: parseFloat(formData.improvements) || 0,
      usefulLife: parseInt(formData.usefulLife) || 0,
      insuranceCoverage: parseFloat(formData.insuranceCoverage) || 0,
      appraisalValue: parseFloat(formData.appraisalValue) || 0,
      incomeGenerated: parseFloat(formData.incomeGenerated) || 0,
      downPayment: parseFloat(formData.downPayment) || 0,
      loanBalance: parseFloat(formData.loanBalance) || 0,
      monthlyPayment: parseFloat(formData.monthlyPayment) || 0,
      interestRate: parseFloat(formData.interestRate) || 0,
      loanTerm: parseInt(formData.loanTerm) || 0,
      squareFootage: parseFloat(formData.squareFootage) || 0,
      yearBuilt: parseInt(formData.yearBuilt) || 0,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseFloat(formData.bathrooms) || 0,
      propertyTaxes: parseFloat(formData.propertyTaxes) || 0,
      hoaFees: parseFloat(formData.hoaFees) || 0,
      rentalIncome: parseFloat(formData.rentalIncome) || 0,
      vehicleYear: parseInt(formData.vehicleYear) || 0,
      mileage: parseInt(formData.mileage) || 0
    };

    // Debug: Log the final assetData being sent
    console.log('🔍 MODAL SUBMIT - Final assetData being sent:', {
      depreciationMethod: assetData.depreciationMethod,
      purchaseDate: assetData.purchaseDate,
      editingAssetId: editingAsset?._id
    });

    onSave(assetData, editingAsset?._id);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      type: 'personal',
      currentValue: '',
      purchasePrice: '',
      purchaseDate: '',
      condition: 'good',
      location: '',
      notes: '',
      isShared: false,
      costBasis: '',
      improvements: '',
      depreciationMethod: 'none',
      usefulLife: '',
      liquidity: 'medium',
      insuranceCoverage: '',
      insuranceProvider: '',
      lastAppraisal: '',
      appraisalValue: '',
      incomeGenerated: '',
      taxClassification: 'personal',
      financingType: 'cash',
      downPayment: '',
      loanBalance: '',
      monthlyPayment: '',
      interestRate: '',
      loanTerm: '',
      lenderName: '',
      propertyAddress: '',
      propertyType: 'single_family',
      squareFootage: '',
      yearBuilt: '',
      bedrooms: '',
      bathrooms: '',
      propertyTaxes: '',
      hoaFees: '',
      rentalIncome: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      vinNumber: '',
      mileage: '',
      licensePlate: ''
    });
    setErrors({});
    setExpandedSections(new Set(['basic']));
    onClose();
  };

  const renderSectionToggle = (sectionKey, title, icon) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
    >
      <div className="flex items-center">
        {icon}
        <span className="ml-2 text-sm font-medium text-gray-700">{title}</span>
      </div>
      {expandedSections.has(sectionKey) ? (
        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
      ) : (
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      )}
    </button>
  );

  const renderInput = (field, label, type = 'text', options = null, placeholder = '') => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {type === 'select' && options ? (
        <select
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors[field] ? 'border-red-300' : 'border-gray-300'
          }`}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === 'checkbox' ? (
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.checked)}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">{label}</span>
        </label>
      ) : type === 'textarea' ? (
        <textarea
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors[field] ? 'border-red-300' : 'border-gray-300'
          }`}
        />
      ) : (
        <input
          type={type}
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors[field] ? 'border-red-300' : 'border-gray-300'
          }`}
        />
      )}
      {errors[field] && (
        <p className="text-sm text-red-600">{errors[field]}</p>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-emerald-50">
          <h2 className="text-xl font-bold text-emerald-800 flex items-center">
            <PlusIcon className="h-6 w-6 mr-2" />
            {editingAsset ? 'Edit Asset' : 'Add New Asset'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-emerald-600" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 p-6 overflow-y-auto pb-8">
          <div className="space-y-6">
            
            {/* Basic Information Section */}
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-emerald-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderInput('name', 'Asset Name *', 'text', null, 'Enter asset name')}
                  {renderInput('type', 'Asset Type', 'select', ASSET_TYPES)}
                  {renderInput('currentValue', 'Current Home Value *', 'number', null, '0')}
                  {renderInput('purchasePrice', 'Purchase Price', 'number', null, '0')}
                  {renderInput('purchaseDate', 'Purchase Date', 'date')}
                  {renderInput('condition', 'Condition', 'select', CONDITION_OPTIONS)}
                  {renderInput('location', 'Location', 'text', null, 'Where is this asset located?')}
                </div>
                <div className="mt-4">
                  {renderInput('notes', 'Notes', 'textarea', null, 'Additional notes about this asset...')}
                </div>
                
                {/* Real-time Equity Calculation for Real Estate */}
                {formData.type === 'real_estate' && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">🏠 Real Estate Equity Calculation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="text-center">
                        <div className="text-gray-600">Home Value</div>
                        <div className="font-semibold text-blue-600">
                          ${formData.currentValue ? parseFloat(formData.currentValue).toLocaleString() : '0'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Mortgage Balance</div>
                        <div className="font-semibold text-red-600">
                          ${formData.loanBalance ? parseFloat(formData.loanBalance).toLocaleString() : '0'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600">Equity</div>
                        <div className="font-bold text-green-600">
                          ${(() => {
                            const homeValue = parseFloat(formData.currentValue) || 0;
                            const mortgageBalance = parseFloat(formData.loanBalance) || 0;
                            const equity = homeValue - mortgageBalance;
                            return equity.toLocaleString();
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-blue-600 text-center">
                      💡 Equity = Home Value - Mortgage Balance
                    </div>
                  </div>
                )}
                
                <div className="mt-4">
                  {renderInput('isShared', 'Shared Asset', 'checkbox')}
                </div>
              </div>
            </div>

            {/* Financing Information Section */}
            <div className="space-y-4">
              {renderSectionToggle('financing', formData.type === 'real_estate' ? 'Mortgage Information' : 'Financing Information', <BanknotesIcon className="h-4 w-4 text-blue-600" />)}
              {expandedSections.has('financing') && (
                <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInput('financingType', 'Financing Type', 'select', FINANCING_TYPES)}
                    {renderInput('downPayment', 'Down Payment', 'number', null, '0')}
                    {renderInput('loanBalance', 'Current Loan Balance', 'number', null, '0')}
                    {renderInput('monthlyPayment', 'Monthly Payment', 'number', null, '0')}
                    {renderInput('interestRate', 'Interest Rate (%)', 'number', null, '0')}
                    {renderInput('loanTerm', 'Loan Term (months)', 'number', null, '0')}
                    {renderInput('lenderName', 'Lender Name', 'text', null, 'Financial institution')}
                  </div>
                </div>
              )}
            </div>

            {/* Real Estate Specific Section */}
            {formData.type === 'real_estate' && (
              <div className="space-y-4">
                {renderSectionToggle('realestate', 'Real Estate Details', <HomeIcon className="h-4 w-4 text-blue-600" />)}
                {expandedSections.has('realestate') && (
                  <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderInput('propertyAddress', 'Property Address', 'text', null, 'Full property address')}
                      {renderInput('propertyType', 'Property Type', 'select', PROPERTY_TYPES)}
                      {renderInput('squareFootage', 'Square Footage', 'number', null, '0')}
                      {renderInput('yearBuilt', 'Year Built', 'number', null, new Date().getFullYear())}
                      {renderInput('bedrooms', 'Bedrooms', 'number', null, '0')}
                      {renderInput('bathrooms', 'Bathrooms', 'number', null, '0')}
                      {renderInput('propertyTaxes', 'Annual Property Taxes', 'number', null, '0')}
                      {renderInput('hoaFees', 'Monthly HOA Fees', 'number', null, '0')}
                      {renderInput('rentalIncome', 'Monthly Rental Income', 'number', null, '0')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Vehicle Specific Section */}
            {formData.type === 'vehicle' && (
              <div className="space-y-4">
                {renderSectionToggle('vehicle', 'Vehicle Details', <TruckIcon className="h-4 w-4 text-green-600" />)}
                {expandedSections.has('vehicle') && (
                  <div className="bg-green-50 p-4 rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Vehicle Make */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Make *
                        </label>
                        <SearchableSelect
                          options={VEHICLE_MAKES}
                          value={formData.vehicleMake}
                          onChange={(value) => setFormData(prev => ({ ...prev, vehicleMake: value }))}
                          placeholder="Select vehicle make..."
                          searchPlaceholder="Search makes..."
                          className="w-full"
                        />
                      </div>

                      {/* Vehicle Model */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Model *
                        </label>
                        <SearchableSelect
                          options={availableModels}
                          value={formData.vehicleModel}
                          onChange={(value) => setFormData(prev => ({ ...prev, vehicleModel: value }))}
                          placeholder={formData.vehicleMake ? "Select model..." : "Select make first"}
                          searchPlaceholder="Search models..."
                          disabled={!formData.vehicleMake}
                          className="w-full"
                          emptyMessage={formData.vehicleMake ? "No models found" : "Select a make first"}
                        />
                      </div>

                      {/* Vehicle Year */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year *
                        </label>
                        <SearchableSelect
                          options={getModelYears().map(year => year.toString())}
                          value={formData.vehicleYear?.toString() || ''}
                          onChange={(value) => setFormData(prev => ({ ...prev, vehicleYear: value }))}
                          placeholder="Select year..."
                          searchPlaceholder="Search years..."
                          className="w-full"
                        />
                      </div>

                      {renderInput('mileage', 'Current Mileage', 'number', null, '0')}
                      {renderInput('vinNumber', 'VIN Number', 'text', null, 'Vehicle identification number')}
                      {renderInput('licensePlate', 'License Plate', 'text', null, 'ABC123')}
                    </div>

                    {/* Auto-Value Estimation */}
                    {showEstimation && estimatedValue && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CalculatorIcon className="h-5 w-5 text-blue-600 mr-2" />
                            <div>
                              <h4 className="text-sm font-medium text-blue-800">Estimated Current Value</h4>
                              <p className="text-xs text-blue-600">
                                Based on {getVehicleCategory(formData.vehicleMake, formData.vehicleModel)} depreciation curve
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-blue-800">
                              ${estimatedValue.toLocaleString()}
                            </p>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, currentValue: estimatedValue.toString() }))}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              Use this value
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Professional Fields Section */}
            <div className="space-y-4">
              {renderSectionToggle('professional', 'Professional & Tax Information', <ChartBarIcon className="h-4 w-4 text-purple-600" />)}
              {expandedSections.has('professional') && (
                <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInput('costBasis', 'Cost Basis', 'number', null, '0')}
                    {renderInput('improvements', 'Improvements Value', 'number', null, '0')}
                    {renderInput('depreciationMethod', 'Depreciation Method', 'select', DEPRECIATION_METHODS)}
                    {renderInput('usefulLife', 'Useful Life (years)', 'number', null, '0')}
                    {renderInput('insuranceCoverage', 'Insurance Coverage', 'number', null, '0')}
                    {renderInput('insuranceProvider', 'Insurance Provider', 'text', null, 'Insurance company name')}
                    {renderInput('lastAppraisal', 'Last Appraisal Date', 'date')}
                    {renderInput('appraisalValue', 'Appraisal Value', 'number', null, '0')}
                    {renderInput('incomeGenerated', 'Monthly Income Generated', 'number', null, '0')}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <PlusIcon className="h-4 w-4 mr-2" />
                {editingAsset ? 'Update Asset' : 'Create Asset'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetCreateModal; 